import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useAnimate } from 'framer-motion';
import { ArrowRightIcon, CheckCircle2Icon, LockIcon, MailIcon } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';
import type { Brand } from '../types/portal';
import { HERO_IMAGE_URL, isHeroReady, preloadHeroImage } from '../utils/heroImage';
interface SignInProps {
  brand: Brand;
  onSignIn: () => void;
  /** True once Continue is clicked: the hero panel widens to fill the screen. */
  expanding?: boolean;
  /** Called when the hero panel fully covers the viewport. */
  onExpanded?: () => void;
}

// Hero expand: ease-in-out, ~600ms. Text/form fade out in 150ms while it widens.
const EXPAND_EASE = [0.65, 0, 0.35, 1] as const;
const EXPAND_DURATION = 0.6;

// One-time code step.
const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 30;
const emptyCode = () => Array<string>(CODE_LENGTH).fill('');
// Demo only: any 6 digits sign in; this one shows the error state.
const DEMO_BAD_CODE = '000000';
export function SignIn({
  brand,
  onSignIn,
  expanding = false,
  onExpanded
}: SignInProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const emailRef = useRef<HTMLInputElement>(null);
  const [code, setCode] = useState<string[]>(emptyCode);
  const [codeError, setCodeError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [resent, setResent] = useState(false);
  const codeRefs = useRef<Array<HTMLInputElement | null>>([]);
  // Photo stays hidden over the navy placeholder until fully decoded, then shows at once (no fade, no partial paint).
  const heroRef = useRef<HTMLImageElement>(null);
  const [heroLoaded, setHeroLoaded] = useState(isHeroReady);

  // Widen the same mounted hero panel (photo + overlays) from its column width to the full viewport.
  const [heroScope, animateHero] = useAnimate<HTMLElement>();
  const onExpandedRef = useRef(onExpanded);
  onExpandedRef.current = onExpanded;
  useLayoutEffect(() => {
    if (!expanding) return;
    const panel = heroScope.current;
    const done = () => onExpandedRef.current?.();
    const from = panel ? panel.getBoundingClientRect().width : 0;
    const to = document.documentElement.clientWidth;
    if (!panel || from === 0) {
      // Hero panel is hidden below the lg breakpoint: just let the form fade, then continue.
      const t = window.setTimeout(done, 150);
      return () => window.clearTimeout(t);
    }
    let cancelled = false;
    animateHero(panel, {
      width: [from, to]
    }, {
      duration: EXPAND_DURATION,
      ease: EXPAND_EASE
    }).then(() => {
      if (!cancelled) done();
    });
    return () => {
      cancelled = true;
    };
  }, [expanding, animateHero, heroScope]);
  useLayoutEffect(() => {
    preloadHeroImage();
    const img = heroRef.current;
    if (img && img.complete && img.naturalWidth > 0) setHeroLoaded(true);
  }, []);

  // Resend cooldown: tick once a second while on the code step.
  useEffect(() => {
    if (status !== 'sent' || cooldown <= 0) return;
    const t = window.setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [status, cooldown]);

  // Move focus to the first code box when the code step opens.
  useEffect(() => {
    if (status === 'sent') codeRefs.current[0]?.focus();
  }, [status]);
  const focusBox = (i: number) => {
    const el = codeRefs.current[Math.max(0, Math.min(CODE_LENGTH - 1, i))];
    el?.focus();
    el?.select();
  };
  const verify = (digits: string[]) => {
    if (expanding || digits.some(d => d === '')) return;
    if (digits.join('') === DEMO_BAD_CODE) {
      // Clear the boxes so the next attempt starts fresh (and a single keystroke can't re-submit a stale code).
      setCode(emptyCode());
      setCodeError('That code didn’t work. Check it and try again.');
      focusBox(0);
      return;
    }
    setCodeError('');
    onSignIn(); // same expand-panel + logo transition into home as before
  };

  // Write digits starting at box `start` (handles typing, paste and OS one-time-code autofill).
  const fillFrom = (start: number, raw: string) => {
    const digits = raw.replace(/\D/g, '').split('');
    if (digits.length === 0) return;
    // A full code always fills from the first box, wherever the caret was.
    const from = digits.length >= CODE_LENGTH ? 0 : start;
    const next = [...code];
    digits.slice(0, CODE_LENGTH - from).forEach((d, k) => {
      next[from + k] = d;
    });
    setCode(next);
    setCodeError('');
    setResent(false);
    const last = Math.min(from + digits.length, CODE_LENGTH) - 1;
    if (next.every(d => d !== '')) {
      focusBox(last);
      verify(next); // auto-submit on the 6th digit
    } else {
      focusBox(last + 1);
    }
  };
  const onCodeKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...code];
      if (next[i]) {
        next[i] = '';
      } else if (i > 0) {
        next[i - 1] = '';
        focusBox(i - 1);
      }
      setCode(next);
      setCodeError('');
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusBox(i + 1);
    }
  };
  const resend = () => {
    if (cooldown > 0) return;
    setCode(emptyCode());
    setCodeError('');
    setResent(true);
    setCooldown(RESEND_COOLDOWN);
    focusBox(0);
  };
  const changeEmail = () => {
    setStatus('idle');
    setCode(emptyCode());
    setCodeError('');
    setResent(false);
    setCooldown(0);
    window.setTimeout(() => {
      emailRef.current?.focus();
      emailRef.current?.select();
    }, 0);
  };
  const codeComplete = code.every(d => d !== '');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Enter the email address your loan officer invited.');
      return;
    }
    setError('');
    setStatus('sending');
    window.setTimeout(() => {
      setCode(emptyCode());
      setCodeError('');
      setResent(false);
      setCooldown(RESEND_COOLDOWN);
      setStatus('sent');
    }, 800);
  };
  return <div className="grid min-h-screen w-full grid-cols-1 overflow-hidden bg-slate-50 lg:grid-cols-[1.1fr_1fr]" data-id="element-372" data-mp="2sul5gsu9ub7">
      <section ref={heroScope} className="relative isolate z-10 hidden flex-col justify-between overflow-hidden bg-ink px-14 pb-28 pt-12 text-white lg:flex" data-id="element-373" data-mp="3tyvyxx46v20j">
        <img ref={heroRef} src={HERO_IMAGE_URL} alt="" aria-hidden="true" decoding="async" loading="eager" draggable={false} onLoad={() => setHeroLoaded(true)} className="pointer-events-none absolute inset-0 -z-20 h-full w-full select-none object-cover" style={{
        opacity: heroLoaded ? 1 : 0
      }} data-id="element-374" data-mp="3k1hwx2j0ifrr" />
        <div aria-hidden="true" className="absolute inset-0 -z-10" style={{
        background: 'linear-gradient(90deg, rgba(22,32,58,0.92) 0%, rgba(22,32,58,0.72) 45%, rgba(22,32,58,0.35) 100%)'
      }} data-id="element-375" data-mp="199gfxob7ohlg" />
        <div aria-hidden="true" className="absolute inset-0 -z-10" style={{
        background: 'linear-gradient(0deg, rgba(22,32,58,0.95) 0%, rgba(22,32,58,0.6) 35%, rgba(22,32,58,0.15) 70%, rgba(22,32,58,0.35) 100%)'
      }} data-id="element-376" data-mp="1qj24svcodj9s" />
        <div className={`animate-fade-up flex flex-col transition-opacity duration-150 ease-out${expanding ? ' opacity-0' : ''}`} data-id="element-377" data-mp="xwpr3p05cgih">
          <BrandLogo brand={brand} tone="light" size="lg" data-id="element-378" data-mp="17sb1obja6p3u" />
        </div>
        {/* Transition logo: centered on the viewport (not the panel, so it doesn't drift while the panel widens).
            Fades + scales in subtly as the panel expands, stays while it's full screen, and fades out with the
            whole photo overlay when the portal is revealed. Not rendered for reduced motion (no expansion). */}
        {expanding && <div aria-hidden="true" className="pointer-events-none absolute top-1/2 z-10" style={{
        left: '50vw',
        transform: 'translate(-50%, -50%)'
      }} data-id="element-379" data-mp="39b4oy9yall8v">
            <div className="animate-hero-logo-in" data-id="element-380" data-mp="3j1zblffgamuu">
              <BrandLogo brand={brand} tone="light" size="lg" data-id="element-381" data-mp="3e9eloozo0twl" />
            </div>
          </div>}
        <div className={`animate-fade-up max-w-xl transition-opacity duration-150 ease-out${expanding ? ' opacity-0' : ''}`} style={{
        '--d': '100ms'
      } as React.CSSProperties} data-id="element-382" data-mp="3s0tcvj3q81eq">
          <p className="text-sm font-medium text-white/70" data-id="element-383" data-mp="5u4jzhzotqpc">Realtor Partner Portal</p>
          <h1 className="mt-4 text-5xl font-semibold leading-[1.1] tracking-tight" data-id="element-384" data-mp="5rs8nnofqel9">
            Your buyers’ loans, answered in seconds.
          </h1>
          <p className="mt-6 max-w-md text-lg leading-relaxed text-white/75" data-id="element-385" data-mp="27mj14eueqr4p">
            Ask about milestones, closing dates and who’s on each file — any time, without waiting on a callback.
          </p>
        </div>
        <figure className={`animate-fade-up max-w-md rounded-2xl bg-white/10 p-5 text-sm transition-opacity duration-150 ease-out${expanding ? ' opacity-0' : ''}`} style={{
        '--d': '220ms'
      } as React.CSSProperties} data-id="element-386" data-mp="i4vx271lq432">
          <p className="text-white/70" data-id="element-387" data-mp="8cgh1f9v7pkt">“When is the Rivera closing?”</p>
          <p className="mt-2 leading-relaxed text-white" data-id="element-388" data-mp="dccib6r6h2wo">
            Estimated closing for Jordan Rivera at 214 Maple Ct is Nov 6. The file is in underwriting.
          </p>
        </figure>
      </section>

      <section aria-hidden={expanding || undefined} className={`flex flex-col items-center justify-center px-6 py-16 transition-opacity duration-150 ease-out${expanding ? ' opacity-0' : ''}`} data-id="element-389" data-mp="3h12sm7y3rnv">
        <div className="animate-fade-up w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 shadow-sm" style={{
        '--d': '120ms'
      } as React.CSSProperties} data-id="element-390" data-mp="3qm9xzioj9i49">
          <div className="lg:hidden" data-id="element-391" data-mp="1orhpoo297pmm">
            <BrandLogo brand={brand} data-id="element-392" data-mp="1oqgduhdeyz3u" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 lg:mt-0 mt-6" data-id="element-393" data-mp="2nltiker9a2rv">Sign in</h2>
          <p className="mt-2 text-sm text-slate-500" data-id="element-394" data-mp="2seecdga5wfve">For realtor partners of {brand.name}.</p>

          {status === 'sent' ? <div className="mt-8" data-id="element-395" data-mp="2ek3ikkjb5vih">
              <div className="animate-fade-up flex gap-3 rounded-xl bg-accent-soft p-4 text-sm text-accent-dark" role="status" data-id="element-396" data-mp="2jkrixwtffopd">
                <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0" data-id="element-397" data-mp="37c6pk1s8ks6f" />
                <div data-id="element-398" data-mp="3bcyzyycuh34a">
                  <p className="font-semibold" data-id="element-399" data-mp="3bbfix5jd6o1g">Check your inbox</p>
                  <p className="mt-1" data-id="element-400" data-mp="2xcxx5dd0rlm6">
                    We sent a sign-in code to <span className="font-medium" data-id="element-401" data-mp="2xes9w4e507f6">{email}</span>. It expires in 10 minutes.
                  </p>
                </div>
              </div>

              <form noValidate onSubmit={e => {
            e.preventDefault();
            verify(code);
          }} className="animate-fade-up mt-6" style={{
            '--d': '60ms'
          } as React.CSSProperties} data-id="element-402" data-mp="1lx99ejrxyikb">
                <p id="code-label" className="text-sm font-medium text-slate-700" data-id="element-403" data-mp="1eesyz5c5k52u">
                  Sign-in code
                </p>
                <div role="group" aria-labelledby="code-label" aria-describedby={codeError ? 'code-error' : undefined} className="mt-2 grid grid-cols-6 gap-2" data-id="element-404" data-mp="1sczpu1iop4t0">
                  {code.map((digit, i) => <input key={i} ref={el => {
                codeRefs.current[i] = el;
              }} type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code" aria-label={`Digit ${i + 1} of ${CODE_LENGTH}`} aria-invalid={!!codeError} value={digit} disabled={expanding} onChange={e => {
                const v = e.target.value;
                // Typing over a filled box: keep only the newly typed digit.
                fillFrom(i, v.length > 1 && v.length <= 2 && digit ? v.replace(digit, '') || v : v);
              }} onKeyDown={e => onCodeKeyDown(i, e)} onPaste={e => {
                e.preventDefault();
                fillFrom(i, e.clipboardData.getData('text'));
              }} onFocus={e => e.target.select()} className={`h-12 w-full min-w-0 rounded-lg border bg-white text-center text-xl font-semibold tabular-nums text-slate-900 caret-accent transition-[border-color,box-shadow] duration-200 ease-out focus:outline-none focus:ring-4 disabled:opacity-70 ${codeError ? 'border-red-400 focus:ring-red-100' : 'border-slate-300 focus:border-accent focus:ring-accent/15'}`} data-id="element-405" data-mp="vp346i8e3jax" data-mpi={i} />)}
                </div>
                {codeError && <p id="code-error" role="alert" className="mt-2 text-sm text-red-600" data-id="element-406" data-mp="y02vuopkm83k">
                    {codeError}
                  </p>}
                <button type="submit" disabled={!codeComplete || expanding} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-200 ease-out hover:bg-brand-dark focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/30 disabled:cursor-not-allowed disabled:opacity-50 hover:-translate-y-px hover:shadow-md active:translate-y-0 disabled:hover:translate-y-0 disabled:hover:bg-brand disabled:hover:shadow-none" data-id="element-407" data-mp="o52klqs1kbwj">
                  Verify &amp; sign in
                  <ArrowRightIcon className="h-4 w-4" data-id="element-408" data-mp="166osgv8a8xcp" />
                </button>
                <div className="mt-4 flex items-center justify-between gap-4 text-sm" data-id="element-409" data-mp="s771c97il806">
                  {cooldown > 0 ? <span className="tabular-nums text-slate-500" aria-live="off" data-id="element-410" data-mp="x4peycwh8bei">
                      Resend in 0:{String(cooldown).padStart(2, '0')}
                    </span> : <button type="button" onClick={resend} disabled={expanding} className="font-semibold text-accent transition-colors duration-200 ease-out hover:text-accent-dark focus:outline-none focus-visible:underline" data-id="element-411" data-mp="n8clng4ufzka">
                      Resend code
                    </button>}
                  <button type="button" onClick={changeEmail} disabled={expanding} className="font-semibold text-accent transition-colors duration-200 ease-out hover:text-accent-dark focus:outline-none focus-visible:underline" data-id="element-412" data-mp="4gjnz8etxq68">
                    Use a different email
                  </button>
                </div>
                <p className="sr-only" role="status" aria-live="polite" data-id="element-413" data-mp="3m1v2wp0a93if">
                  {resent ? `New code sent to ${email}.` : ''}
                </p>
                {resent && <p className="animate-fade-up mt-2 text-xs text-slate-500" data-id="element-414" data-mp="3uzox9uehln2y">New code sent to {email}.</p>}
              </form>
            </div> : <form onSubmit={submit} noValidate className="mt-8" data-id="element-415" data-mp="8l5jnzxc6bxj">
              <label htmlFor="email" className="text-sm font-medium text-slate-700" data-id="element-416" data-mp="8myr3hfrx06l">
                Email
              </label>
              <input ref={emailRef} id="email" type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@brokerage.com" aria-invalid={!!error} aria-describedby={error ? 'email-error' : undefined} className={`mt-2 block w-full rounded-lg border px-3.5 transition-[border-color,box-shadow] duration-200 ease-out py-2.5 text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 ${error ? 'border-red-400 focus:ring-red-100' : 'border-slate-300 focus:border-accent focus:ring-accent/15'}`} data-id="element-417" data-mp="ijbig8nuj1y6" />
              {error && <p id="email-error" className="mt-2 text-sm text-red-600" data-id="element-418" data-mp="1n9qgibsbrx0m">
                  {error}
                </p>}
              <button type="submit" disabled={status === 'sending'} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-4 py-3 text-sm font-semibold text-white transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:bg-brand-dark focus:outline-none focus-visible:ring-4 focus-visible:ring-accent/30 disabled:opacity-70 hover:-translate-y-px hover:shadow-md active:translate-y-0 disabled:hover:translate-y-0 disabled:hover:shadow-none" data-id="element-419" data-mp="3k3lk9wmiotz8">
                <MailIcon className="h-4 w-4" data-id="element-420" data-mp="2p5ef1wt7v5vl" />
                {status === 'sending' ? 'Sending code…' : 'Email me a sign-in code'}
              </button>
            </form>}

          <p className="mt-6 flex items-center gap-2 text-sm text-slate-500" data-id="element-421" data-mp="2g46hxi2fsb33">
            <LockIcon className="h-4 w-4 shrink-0 text-slate-400" data-id="element-422" data-mp="2g69i2uxazklj" />
            Access is by invitation from your loan officer.
          </p>

          {/* Demo shortcut on both the email and code steps: same expand + logo transition, no email or code needed. */}
          <div className="mt-8 border-t border-slate-100 pt-6" data-id="element-423" data-mp="zolpen0u7ckm">
              <button type="button" onClick={onSignIn} disabled={expanding} className="group inline-flex items-center gap-1.5 text-sm font-semibold text-accent transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-out hover:text-accent-dark focus:outline-none focus-visible:underline" data-id="element-424" data-mp="2zs6f98uhsdb1">
                Continue (demo)
                <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" data-id="element-425" data-mp="3ijfqpnntpc59" />
              </button>
            </div>

        </div>
        <p className="mt-8 text-center text-xs text-slate-500" data-id="element-426" data-mp="38s2xarmdmdx4">{brand.legal}</p>
      </section>
    </div>;
}